import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Mic, MicOff, Video, VideoOff, Settings } from "lucide-react";
import { AudioLevelMeter } from "./AudioLevelMeter";
import { cn } from "@/lib/utils";

export interface PreJoinChoice {
    micOn: boolean;
    camOn: boolean;
    cameraId?: string;
    micId?: string;
    speakerId?: string;
    displayName?: string;
}

interface PreJoinProps {
    subject: string;
    /** Audio-only calls hide every camera control. */
    audioOnly?: boolean;
    /** Guests must type a name; signed-in users already have one. */
    askForName?: boolean;
    /** Prefills the name (a returning guest). */
    initialName?: string;
    joinLabel?: string;
    onJoin: (choice: PreJoinChoice) => void;
    onCancel?: () => void;
}

/**
 * The "green room" shown before entering a meeting — Teams' pre-join screen.
 *
 * Signed-in users previously had none: clicking a meeting acquired the camera
 * and dropped them straight into the call, live, with whatever device the
 * browser picked. Only anonymous guests got a preview. This is the shared
 * screen for both, so everyone can see themselves, pick a camera / microphone
 * / speaker, and decide what is on BEFORE anyone else can see or hear them.
 *
 * It owns a preview stream and always stops it before handing control back —
 * a camera left open here would make the real acquisition in MeetingRoom fail
 * on the devices that only allow a single consumer.
 */
export function PreJoin({
    subject,
    audioOnly = false,
    askForName = false,
    initialName,
    joinLabel = "Join now",
    onJoin,
    onCancel
}: PreJoinProps) {
    const [micOn, setMicOn] = useState(true);
    const [camOn, setCamOn] = useState(!audioOnly);
    const [name, setName] = useState(initialName ?? "");
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [cameraId, setCameraId] = useState<string | undefined>();
    const [micId, setMicId] = useState<string | undefined>();
    const [speakerId, setSpeakerId] = useState<string | undefined>();
    const [showSettings, setShowSettings] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [starting, setStarting] = useState(true);

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);

    const stopPreview = useCallback(() => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setPreviewStream(null);
    }, []);

    // Acquire (and re-acquire on device change) the preview.
    useEffect(() => {
        let cancelled = false;
        setStarting(true);
        // Drop the previous stream first — switching camera while the old one
        // is still open fails outright on most Windows webcam drivers.
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;

        navigator.mediaDevices
            .getUserMedia({
                audio: micId ? { deviceId: { exact: micId } } : true,
                video: audioOnly ? false : cameraId ? { deviceId: { exact: cameraId } } : true
            })
            .then((stream) => {
                if (cancelled) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }
                streamRef.current = stream;
                setPreviewStream(stream);
                setError(null);
                setStarting(false);
                // Labels are empty until permission is granted, so enumerate
                // only after getUserMedia has resolved.
                void navigator.mediaDevices.enumerateDevices().then((d) => {
                    if (!cancelled) setDevices(d);
                });
            })
            .catch((e) => {
                if (cancelled) return;
                setStarting(false);
                setError(e instanceof Error ? e.message : "Camera / microphone unavailable.");
            });

        return () => {
            cancelled = true;
        };
    }, [cameraId, micId, audioOnly]);

    // Bind via callback ref: the element unmounts whenever the camera is
    // toggled off, so an effect keyed on the stream would leave it black.
    const bindVideo = useCallback((el: HTMLVideoElement | null) => {
        videoRef.current = el;
        if (el && streamRef.current) el.srcObject = streamRef.current;
    }, []);
    useEffect(() => {
        if (videoRef.current && previewStream) videoRef.current.srcObject = previewStream;
    }, [previewStream]);

    // Mirror the toggles onto the live preview.
    useEffect(() => {
        streamRef.current?.getAudioTracks().forEach((t) => { t.enabled = micOn; });
    }, [micOn, previewStream]);
    useEffect(() => {
        streamRef.current?.getVideoTracks().forEach((t) => { t.enabled = camOn; });
    }, [camOn, previewStream]);

    // Release devices if the component goes away without joining.
    useEffect(() => () => stopPreview(), [stopPreview]);

    const cameras = devices.filter((d) => d.kind === "videoinput");
    const mics = devices.filter((d) => d.kind === "audioinput");
    const speakers = devices.filter((d) => d.kind === "audiooutput");
    const nameMissing = askForName && name.trim().length === 0;

    const join = () => {
        if (nameMissing) return;
        // Hand the devices over cleanly — MeetingRoom re-acquires them.
        stopPreview();
        onJoin({
            micOn,
            camOn: audioOnly ? false : camOn,
            cameraId,
            micId,
            speakerId,
            displayName: askForName ? name.trim() : undefined
        });
    };

    return (
        <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-zinc-950 p-4 text-white sm:p-6">
            <div className="w-full max-w-md text-center">
                <div className="text-xs uppercase tracking-wider text-zinc-400">Ready to join?</div>
                <h1 className="truncate text-lg font-semibold">{subject}</h1>
            </div>

            {/* Preview */}
            <div className="relative aspect-video w-full max-w-md overflow-hidden rounded-xl border border-white/10 bg-zinc-900">
                {!audioOnly && camOn && !error ? (
                    <video ref={bindVideo} autoPlay playsInline muted className="h-full w-full scale-x-[-1] object-cover" />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-sm text-zinc-400">
                        {starting ? (
                            <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Starting camera…</span>
                        ) : error ? (
                            <span className="px-6 text-center text-amber-300">{error}</span>
                        ) : (
                            "Camera is off"
                        )}
                    </div>
                )}

                {/* Mic level so the user can confirm the mic actually works. */}
                <div className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-md bg-black/60 px-2 py-1 text-xs">
                    {micOn ? <Mic className="h-3.5 w-3.5" /> : <MicOff className="h-3.5 w-3.5 text-red-400" />}
                    <AudioLevelMeter stream={previewStream} muted={!micOn} bars={4} className="text-emerald-400" />
                </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => setMicOn((v) => !v)}
                    aria-pressed={micOn}
                    aria-label={micOn ? "Turn microphone off" : "Turn microphone on"}
                    className={cn(
                        "flex h-11 w-11 items-center justify-center rounded-full border border-white/15",
                        micOn ? "bg-white/10 hover:bg-white/20" : "bg-red-500/90 hover:bg-red-500"
                    )}
                >
                    {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
                </button>
                {!audioOnly ? (
                    <button
                        type="button"
                        onClick={() => setCamOn((v) => !v)}
                        aria-pressed={camOn}
                        aria-label={camOn ? "Turn camera off" : "Turn camera on"}
                        className={cn(
                            "flex h-11 w-11 items-center justify-center rounded-full border border-white/15",
                            camOn ? "bg-white/10 hover:bg-white/20" : "bg-red-500/90 hover:bg-red-500"
                        )}
                    >
                        {camOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
                    </button>
                ) : null}
                <button
                    type="button"
                    onClick={() => setShowSettings((v) => !v)}
                    aria-expanded={showSettings}
                    aria-label="Device settings"
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/10 hover:bg-white/20"
                >
                    <Settings className="h-5 w-5" />
                </button>
            </div>

            {showSettings ? (
                <div className="w-full max-w-md space-y-2 rounded-lg border border-white/10 bg-zinc-900 p-3 text-sm">
                    {!audioOnly ? (
                        <DevicePicker
                            label="Camera"
                            devices={cameras}
                            value={cameraId}
                            onChange={setCameraId}
                            fallbackLabel="Camera"
                        />
                    ) : null}
                    <DevicePicker label="Microphone" devices={mics} value={micId} onChange={setMicId} fallbackLabel="Microphone" />
                    {speakers.length > 0 ? (
                        <DevicePicker label="Speaker" devices={speakers} value={speakerId} onChange={setSpeakerId} fallbackLabel="Speaker" />
                    ) : null}
                </div>
            ) : null}

            {askForName ? (
                <input
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") join(); }}
                    placeholder="Your name"
                    aria-label="Your name"
                    className="h-10 w-full max-w-md rounded-md border border-white/15 bg-zinc-900 px-3 text-sm outline-none focus:border-primary"
                />
            ) : null}

            <div className="flex w-full max-w-md items-center gap-2">
                {onCancel ? (
                    <button
                        type="button"
                        onClick={() => { stopPreview(); onCancel(); }}
                        className="h-10 flex-1 rounded-md border border-white/15 text-sm hover:bg-white/10"
                    >
                        Cancel
                    </button>
                ) : null}
                <button
                    type="button"
                    onClick={join}
                    disabled={nameMissing}
                    className="h-10 flex-1 rounded-md bg-primary text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                    {joinLabel}
                </button>
            </div>

            {error ? (
                <p className="max-w-md text-center text-xs text-zinc-400">
                    You can still join without a camera or microphone — others just won't see or hear you.
                </p>
            ) : null}
        </div>
    );
}

function DevicePicker({ label, devices, value, onChange, fallbackLabel }: {
    label: string;
    devices: MediaDeviceInfo[];
    value: string | undefined;
    onChange: (id: string) => void;
    fallbackLabel: string;
}) {
    return (
        <label className="flex items-center gap-2">
            <span className="w-24 shrink-0 text-xs text-zinc-400">{label}</span>
            <select
                value={value ?? devices[0]?.deviceId ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="h-8 min-w-0 flex-1 rounded-md border border-white/15 bg-zinc-800 px-2 text-xs outline-none"
            >
                {devices.length === 0 ? <option value="">No device found</option> : null}
                {devices.map((d, i) => (
                    <option key={d.deviceId || i} value={d.deviceId}>
                        {d.label || `${fallbackLabel} ${i + 1}`}
                    </option>
                ))}
            </select>
        </label>
    );
}
